// src/services/marketDataService.ts
import axios from 'axios';

// For demo purposes, we'll use a free API endpoint
// In a real application, you would replace this with your chosen financial API

// Interface for market data
export interface MarketData {
  id: number;
  name: string;
  symbol: string;
  value: string;
  change: string;
  changePercent: string;
  positive: boolean;
}

// Interface for instrument data
export interface Instrument {
  id: number;
  name: string;
  symbol: string;
  category: string;
}

// Mock data for demonstration
const mockMarketData: MarketData[] = [
  { id: 1, name: 'S&P 500', symbol: 'SPX', value: '4,325.87', change: '+32.45', changePercent: '+0.75%', positive: true },
  { id: 2, name: 'Dow Jones', symbol: 'DJI', value: '34,268.91', change: '+109.34', changePercent: '+0.32%', positive: true },
  { id: 3, name: 'NASDAQ', symbol: 'IXIC', value: '14,521.36', change: '+174.62', changePercent: '+1.21%', positive: true },
  { id: 4, name: 'Gold', symbol: 'XAU', value: '$1,812.45', change: '+8.12', changePercent: '+0.45%', positive: true },
  { id: 5, name: 'Oil (WTI)', symbol: 'CL', value: '$73.28', change: '-0.16', changePercent: '-0.21%', positive: false },
  { id: 6, name: 'EUR/USD', symbol: 'EURUSD', value: '1.1842', change: '+0.0014', changePercent: '+0.12%', positive: true },
];

const mockInstruments: Instrument[] = [
  // Indices
  { id: 1, name: 'S&P 500', symbol: 'SPX', category: 'indices' },
  { id: 2, name: 'Dow Jones', symbol: 'DJI', category: 'indices' },
  { id: 3, name: 'NASDAQ', symbol: 'IXIC', category: 'indices' },
  { id: 4, name: 'FTSE 100', symbol: 'UKX', category: 'indices' },
  { id: 5, name: 'DAX', symbol: 'DAX', category: 'indices' },
  
  // Currencies
  { id: 6, name: 'Euro', symbol: 'EUR/USD', category: 'currency' },
  { id: 7, name: 'British Pound', symbol: 'GBP/USD', category: 'currency' },
  { id: 8, name: 'Japanese Yen', symbol: 'USD/JPY', category: 'currency' },
  { id: 9, name: 'Swiss Franc', symbol: 'USD/CHF', category: 'currency' },
  { id: 10, name: 'Australian Dollar', symbol: 'AUD/USD', category: 'currency' },
  
  // Commodities
  { id: 11, name: 'Gold', symbol: 'XAU/USD', category: 'commodities' },
  { id: 12, name: 'Silver', symbol: 'XAG/USD', category: 'commodities' },
  { id: 13, name: 'Crude Oil', symbol: 'WTI', category: 'commodities' },
  { id: 14, name: 'Natural Gas', symbol: 'NGAS', category: 'commodities' },
  { id: 15, name: 'Coffee', symbol: 'KC', category: 'commodities' },
  
  // Stocks
  { id: 16, name: 'Apple', symbol: 'AAPL', category: 'stocks' },
  { id: 17, name: 'Microsoft', symbol: 'MSFT', category: 'stocks' },
  { id: 18, name: 'Amazon', symbol: 'AMZN', category: 'stocks' },
  { id: 19, name: 'Google', symbol: 'GOOGL', category: 'stocks' },
  { id: 20, name: 'Tesla', symbol: 'TSLA', category: 'stocks' },
];

// Function to fetch market data (mock implementation)
export const fetchMarketData = async (): Promise<MarketData[]> => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 800));
  
  // In a real application, you would make an API call like this:
  // const response = await axios.get('https://api.example.com/market-data');
  // return response.data;
  
  // For now, we'll return mock data
  return mockMarketData;
};

// Function to fetch instruments (mock implementation)
export const fetchInstruments = async (): Promise<Instrument[]> => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 500));
  
  // In a real application, you would make an API call like this:
  // const response = await axios.get('https://api.example.com/instruments');
  // return response.data;
  
  // For now, we'll return mock data
  return mockInstruments;
};

// Function to fetch real-time updates (mock implementation)
export const subscribeToMarketUpdates = (callback: (data: MarketData[]) => void) => {
  // In a real application, you would use WebSocket or similar technology:
  // const ws = new WebSocket('wss://api.example.com/market-updates');
  // ws.onmessage = (event) => {
  //   const data = JSON.parse(event.data);
  //   callback(data);
  // };
  
  // For demonstration, we'll simulate updates with random data
  const interval = setInterval(() => {
    const updatedData = mockMarketData.map(item => {
      // Generate small random changes for demonstration
      const change = (Math.random() - 0.5) * 2;
      const currentValue = parseFloat(item.value.replace(/[^0-9.]/g, ''));
      const newValue = currentValue + change;
      const newChange = change;
      const changePercent = (change / currentValue * 100).toFixed(2);
      
      return {
        ...item,
        value: item.symbol.includes('$') ? `$${newValue.toFixed(2)}` : newValue.toFixed(2),
        change: `${newChange >= 0 ? '+' : ''}${newChange.toFixed(2)}`,
        changePercent: `${newChange >= 0 ? '+' : ''}${changePercent}%`,
        positive: newChange >= 0
      };
    });
    
    callback(updatedData);
  }, 10000); // Update every 10 seconds
  
  // Return a function to unsubscribe
  return () => clearInterval(interval);
};